import { useState, useRef, useEffect } from 'react';
import { Sun, Moon, Monitor, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useThemeStore, type Theme, type AccentColor } from '@/stores/theme.store';

const THEME_OPTIONS: { value: Theme; label: string; Icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', Icon: Sun },
  { value: 'dark', label: 'Dark', Icon: Moon },
  { value: 'system', label: 'System', Icon: Monitor },
];

const ACCENT_OPTIONS: { value: AccentColor; label: string; swatch: string }[] = [
  { value: 'blue', label: 'Blue', swatch: 'bg-blue-500' },
  { value: 'violet', label: 'Violet', swatch: 'bg-violet-500' },
  { value: 'emerald', label: 'Emerald', swatch: 'bg-emerald-500' },
  { value: 'rose', label: 'Rose', swatch: 'bg-rose-500' },
  { value: 'amber', label: 'Amber', swatch: 'bg-amber-500' },
  { value: 'cyan', label: 'Cyan', swatch: 'bg-cyan-500' },
];

export function ThemeSwitcher() {
  const { theme, accentColor, setTheme, setAccentColor } = useThemeStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isDark =
    theme === 'dark' ||
    (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape key
  useEffect(() => {
    if (!isOpen) return;
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setIsOpen(false);
    }
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [isOpen]);

  const CurrentIcon = isDark ? Moon : Sun;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 rounded-lg p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="Theme settings"
        title="Theme settings"
      >
        <CurrentIcon className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1 w-56 animate-fade-in rounded-xl border bg-popover p-3 shadow-lg z-50">
          {/* Theme mode toggle */}
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Theme
          </p>
          <div className="flex gap-1 rounded-lg bg-muted p-1">
            {THEME_OPTIONS.map(({ value, label, Icon }) => (
              <button
                key={value}
                onClick={() => setTheme(value)}
                className={cn(
                  'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition-all',
                  theme === value
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
                title={label}
              >
                <Icon className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            ))}
          </div>

          {/* Accent color picker */}
          <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Accent Color
          </p>
          <div className="grid grid-cols-6 gap-2">
            {ACCENT_OPTIONS.map(({ value, label, swatch }) => (
              <button
                key={value}
                onClick={() => setAccentColor(value)}
                className={cn(
                  'group flex h-8 w-8 items-center justify-center rounded-full transition-all',
                  accentColor === value
                    ? 'ring-2 ring-offset-2 ring-offset-popover ring-current'
                    : 'hover:scale-110',
                )}
                title={label}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full transition-transform',
                    swatch,
                  )}
                >
                  {accentColor === value && (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
